/**
 * 等距实体预览渲染模块。
 * 
 * 职责：从 SketchXxxObject 实例中提取几何数据，生成轻量 Three.js Line 对象
 * 用于"等距实体"命令的实时预览渲染。与偏移计算（SketchEntityEquidistantOffset）完全解耦。
 * 
 * 线型说明：
 * 1. 每个预览生成函数均支持实线 / 虚线两种样式，通过 dashed 参数切换；
 * 2. 实线复用 FeaturePreviewLineMaterialFactory 的单例材质；
 * 3. 虚线使用 ScreenSpaceDashedLineNodeMaterial（WebGPU 下正确渲染屏幕空间虚线，
 *    缩放后虚线节奏不变），且每条预览线必须独享一个材质实例，
 *    避免不同折线互相覆盖 points uniform。
 */

import {
  BufferGeometry,
  Vector2,
  Vector3,
  Line,
  EllipseCurve,
  Shape,
  type LineBasicMaterial,
} from 'three/webgpu';

import {
  SketchLineObject,
  SketchArcObject,
  SketchCircleObject,
  SketchEllipseObject,
  SketchParabolaObject,
  SketchSplineObject,
  SketchStyleSplineObject,
  SketchOverlayRenderStateApplier,
} from '../../mesh';
import {
  FeaturePreviewLineMaterialFactory,
  LineMaterialFactory,
  SketchLineMaterialDepthPolicy,
  syncScreenSpaceDashedLineGeometry,
  type ScreenSpaceDashedLineNodeMaterial,
} from '../../material';
import type { ReferencePlane } from '../../reference';
import { SketchOffsetCoverFactory } from './SketchOffsetCoverFactory';
import type { CoverType } from './type';

/** 预览线颜色（与特征预览描边材质工厂保持一致）。 */
const PREVIEW_LINE_COLOR = 0xffff00;
/** 预览虚线屏幕空间虚线段长度（与 LineMaterialFactory 预览虚线节奏保持一致）。 */
const PREVIEW_DASH_SIZE = 3;
/** 预览虚线屏幕空间虚线间隔长度。 */
const PREVIEW_GAP_SIZE = 2;

class SketchIsometricEntitiesPreview {

  /**
   * 创建预览线材质。
   *
   * 说明：
   * 1. 实线复用 FeaturePreviewLineMaterialFactory 单例材质，避免重复创建；
   * 2. 虚线复用 LineMaterialFactory.createScreenSpaceDashedLineMaterial 统一接口，
   *    该接口内部会应用 SketchLineMaterialDepthPolicy（关闭深度测试与深度写入），
   *    与全项目草图线/绘制预览线保持一致的前景覆盖语义；
   * 3. 每条预览线独享一个材质实例，防止不同折线互相覆盖 points uniform；
   * 4. 虚线必须 transparent: true（opacity 1）进入透明渲染队列：
   *    上色草图轮廓是 transparent + opacity 0.8 的半透明面，透明对象统一
   *    在不透明对象之后按 renderOrder 排序绘制；若虚线保持不透明，会先被
   *    画进不透明通道，随后被轮廓面混合叠加导致颜色被洗淡。
   *    虚线 renderOrder（COMMAND_PREVIEW_RENDER_ORDER=650）高于轮廓面
   *    （CONTOUR_RENDER_ORDER=180），同处透明队列时始终绘制在轮廓之上。
   *
   * @param points 预览折线点列（世界坐标），虚线材质内部持有其 uniform 引用。
   * @param dashed 是否使用虚线样式。
   * @returns 预览线材质。
   */
  private static createPreviewMaterial(points: Vector3[], dashed: boolean): LineBasicMaterial | ScreenSpaceDashedLineNodeMaterial {
    if (!dashed) {
      return FeaturePreviewLineMaterialFactory.getMaterial();
    }
    return LineMaterialFactory.createScreenSpaceDashedLineMaterial({
      points,
      color: PREVIEW_LINE_COLOR,
      linewidth: 1,
      dashSize: PREVIEW_DASH_SIZE,
      gapSize: PREVIEW_GAP_SIZE,
      opacity: 1,
      transparent: true,
      name: '等距实体预览-屏幕空间虚线材质',
    });
  }

  /**
   * 创建预览 Line 对象。
   *
   * 说明：
   * 1. 统一完成几何构建、材质选择与虚线几何同步，避免各生成函数重复实现；
   * 2. 屏幕空间虚线材质依赖 lineDistancePercent attribute 与 points uniform，
   *    创建后必须立即调用 syncScreenSpaceDashedLineGeometry 完成同步；
   * 3. 等距实体预览属于命令待确认反馈，统一应用草图线深度策略
   *    （关闭深度测试与深度写入），并提升到命令预览渲染层级
   *    （{@link SketchOverlayRenderStateApplier.COMMAND_PREVIEW_RENDER_ORDER}），
   *    保证与共面的实体面、草图边同处场景时始终作为前景可见，
   *    不被后绘制的实体面覆盖（与草图圆角预览口径一致）；
   * 4. 虚线材质经 {@link createPreviewMaterial} 进入透明渲染队列
   *    （transparent: true，opacity 1），renderOrder 650 高于上色草图轮廓面
   *    的 180，保证虚线绘制在半透明轮廓面之上而不被其洗淡。
   *
   * @param points 预览折线点列（世界坐标）。
   * @param dashed 是否使用虚线样式。
   * @returns 预览用 Three.js Line 对象。
   */
  private static createPreviewLine(points: Vector3[], dashed: boolean): Line {
    const geometry = new BufferGeometry().setFromPoints(points);
    const material = this.createPreviewMaterial(points, dashed);
    // 实线/虚线两种材质入口统一收口到草图线深度策略，
    // 即使外部材质重建（如 FeaturePreviewLineMaterialFactory 单例被替换）也不回退默认深度状态。
    SketchLineMaterialDepthPolicy.apply(material);
    const line = new Line(geometry, material);
    // 预览线必须绘制在实体与正式草图对象之后，保证前景覆盖语义。
    line.renderOrder = SketchOverlayRenderStateApplier.COMMAND_PREVIEW_RENDER_ORDER;
    if (dashed) {
      // Node 虚线材质依赖距离百分比 attribute，创建后必须立即同步一次几何距离。
      syncScreenSpaceDashedLineGeometry(line);
    }
    return line;
  }

  /**
   * 从偏移后的线段对象生成预览 Line。
   * @param lineObject 偏移后的 SketchLineObject 实例。
   * @param dashed 是否使用虚线样式，默认 false 保持实线。
   * @returns 预览用 Three.js Line 对象。
   */
  static createLinePreview(lineObject: SketchLineObject, dashed = false): Line {
    const start = lineObject.startPoint.position;
    const end = lineObject.endPoint.position;
    return this.createPreviewLine([start, end], dashed);
  }

  /**
   * 从偏移后的圆弧对象生成预览 Line。
   * 
   * 说明：通过 getArcDefinition() 获取几何定义，
   * 使用 EllipseCurve 在参考面局部坐标系中采样，再映射回世界坐标。
   * 
   * @param arcObject 偏移后的 SketchArcObject 实例。
   * @param referencePlane 参考平面。
   * @param dashed 是否使用虚线样式，默认 false 保持实线。
   * @returns 预览用 Three.js Line 对象。
   */
  static createArcPreview(arcObject: SketchArcObject, referencePlane: ReferencePlane, dashed = false): Line {
    const definition = arcObject.getArcDefinition();
    const { centerWorld, radius, startAngle, endAngle, clockwise, segments = 64 } = definition;

    // 将圆心转换到参考平面局部坐标
    const localCenter = referencePlane.worldToLocal(centerWorld.clone());

    // 使用 EllipseCurve 在局部坐标系中生成弧线采样点
    const curve = new EllipseCurve(
      localCenter.x,
      localCenter.y,
      radius,
      radius,
      startAngle,
      endAngle,
      clockwise,
      0,
    );

    const localPoints = curve.getPoints(segments);

    // 将局部坐标采样点映射回世界坐标
    const worldPoints = localPoints.map((point) =>
      referencePlane.localToWorld(new Vector2(point.x, point.y)),
    );

    return this.createPreviewLine(worldPoints, dashed);
  }

  /**
   * 从偏移后的圆对象生成预览 Line。
   * 
   * 说明：使用 THREE.Shape.absarc() + getPoints() 采样完整圆。
   * 
   * @param circleObject 偏移后的 SketchCircleObject 实例。
   * @param referencePlane 参考平面。
   * @param dashed 是否使用虚线样式，默认 false 保持实线。
   * @returns 预览用 Three.js Line 对象。
   */
  static createCirclePreview(circleObject: SketchCircleObject, referencePlane: ReferencePlane, dashed = false): Line {
    const center = circleObject.vec3dCenter;
    const radius = circleObject.radius;

    const localCenter = referencePlane.worldToLocal(center.clone());

    // 使用 Shape 生成完整圆采样点
    const shape = new Shape();
    shape.absarc(localCenter.x, localCenter.y, radius, 0, Math.PI * 2, false);

    const segments = 64;
    const localPoints = shape.getPoints(segments);

    const worldPoints = localPoints.map((point) =>
      referencePlane.localToWorld(new Vector2(point.x, point.y)),
    );

    return this.createPreviewLine(worldPoints, dashed);
  }

  /**
   * 从偏移后的椭圆对象生成预览 Line。
   * 
   * 说明：通过 getEllipseDefinition() 获取几何定义，
   * 使用 EllipseCurve 在参考面局部坐标系中采样（含 rotation 旋转参数）。
   * 
   * @param ellipseObject 偏移后的 SketchEllipseObject 实例。
   * @param referencePlane 参考平面。
   * @param dashed 是否使用虚线样式，默认 false 保持实线。
   * @returns 预览用 Three.js Line 对象。
   */
  static createEllipsePreview(ellipseObject: SketchEllipseObject, referencePlane: ReferencePlane, dashed = false): Line {
    const definition = ellipseObject.getEllipseDefinition();
    const { centerWorld, radiusX, radiusY, rotation, startAngle, endAngle, clockwise, segments = 64 } = definition;

    const localCenter = referencePlane.worldToLocal(centerWorld.clone());

    // 使用 EllipseCurve 在局部坐标系中生成椭圆弧采样点（含 rotation 参数）
    const curve = new EllipseCurve(
      localCenter.x,
      localCenter.y,
      radiusX,
      radiusY,
      startAngle,
      endAngle,
      clockwise,
      rotation,
    );

    const localPoints = curve.getPoints(segments);

    const worldPoints = localPoints.map((point) =>
      referencePlane.localToWorld(new Vector2(point.x, point.y)),
    );

    return this.createPreviewLine(worldPoints, dashed);
  }

  /**
   * 从偏移后的抛物线对象生成预览 Line。
   * 
   * 说明：抛物线对象内置了曲线采样点（getCurvePoints()），
   * 无需通过参考面重新采样，直接使用世界坐标点列创建 Line。
   * 
   * @param parabolaObject 偏移后的 SketchParabolaObject 实例。
   * @param dashed 是否使用虚线样式，默认 false 保持实线。
   * @returns 预览用 Three.js Line 对象。
   */
  static createParabolaPreview(parabolaObject: SketchParabolaObject, dashed = false): Line {
    const curvePoints = parabolaObject.getCurvePoints();
    return this.createPreviewLine(curvePoints, dashed);
  }

  /**
   * 从偏移后的样条对象生成预览 Line（通用样条 & 样式样条）。
   * 
   * 说明：样条对象内置了密集采样点（getCurvePoints()），
   * 直接使用世界坐标点列创建 Line。
   * 
   * @param splineObject 偏移后的 SketchSplineObject 或 SketchStyleSplineObject 实例。
   * @param dashed 是否使用虚线样式，默认 false 保持实线。
   * @returns 预览用 Three.js Line 对象。
   */
  static createSplinePreview(splineObject: SketchSplineObject | SketchStyleSplineObject, dashed = false): Line {
    const curvePoints = splineObject.getCurvePoints();
    return this.createPreviewLine(curvePoints, dashed);
  }

  /**
   * 从顶端加盖的端点生成盖预览 Line（直线盖/圆弧盖）。
   *
   * 说明：
   * 1. 盖预览只依赖两端点世界坐标与胶囊外侧方向，不创建真实草图实体，
   *    避免预览阶段污染草图组（选择/约束求解/历史快照）；
   * 2. 圆弧盖复用 {@link SketchOffsetCoverFactory} 的半圆定义计算
   *    （圆心=中点、半径=弦长一半、鼓包朝外侧），与落地盖子几何口径完全一致；
   * 3. 圆弧盖在参考面局部 2D 用 EllipseCurve 采样后映射回世界坐标
   *    （与 {@link createArcPreview} 同一采样模式，默认 32 段）；
   * 4. 两端点重合（如偏移距离为 0）时半径为零，EllipseCurve 会产出 NaN，
   *    降级为两点直线（视觉上等同重合点，保证不产生脏几何）；
   * 5. 复用 {@link createPreviewLine}：实线共享单例材质、虚线独享材质，
   *    深度策略与命令预览层级（renderOrder=650）自动继承，无需额外渲染配置。
   *
   * @param coverType 盖类型（圆弧盖为半圆，直线盖为线段）。
   * @param referencePlane 参考面。
   * @param startWorld 盖端点 A 世界坐标。
   * @param endWorld 盖端点 B 世界坐标。
   * @param outwardWorld 圆弧盖胶囊外侧方向（世界坐标）；直线盖不使用。
   * @param dashed 是否使用虚线样式（构造几何体开启时 true）。
   * @returns 预览用 Three.js Line 对象。
   */
  static createCoverPreview(
    coverType: CoverType,
    referencePlane: ReferencePlane,
    startWorld: Vector3,
    endWorld: Vector3,
    outwardWorld: Vector3 | undefined,
    dashed = false,
  ): Line {
    // 直线盖：直接连接两端点
    if (coverType === 'straight line') {
      return this.createPreviewLine([startWorld, endWorld], dashed);
    }

    // 圆弧盖：复用落地工厂的半圆定义计算，保证预览与落地几何一致
    const clockwise = SketchOffsetCoverFactory.resolveSemiCircleClockwise(
      referencePlane,
      startWorld,
      endWorld,
      outwardWorld,
    );
    const { centerWorld, radius, startAngle, endAngle } = SketchOffsetCoverFactory.computeSemiCircleDefinition(
      referencePlane,
      startWorld,
      endWorld,
      clockwise,
    );

    // 退化防护：偏移距离为 0 时两端点重合、半径为零，
    // EllipseCurve 采样会产生 NaN 点，降级为两点直线
    if (radius < 1e-12) {
      return this.createPreviewLine([startWorld, endWorld], dashed);
    }

    // 在参考面局部 2D 采样半圆后映射回世界坐标（与 createArcPreview 同一模式）
    const localCenter = referencePlane.worldToLocal(centerWorld.clone());
    const curve = new EllipseCurve(
      localCenter.x,
      localCenter.y,
      radius,
      radius,
      startAngle,
      endAngle,
      clockwise,
      0,
    );
    const localPoints = curve.getPoints(32);
    const worldPoints = localPoints.map((point) =>
      referencePlane.localToWorld(new Vector2(point.x, point.y)),
    );
    return this.createPreviewLine(worldPoints, dashed);
  }
}

export { SketchIsometricEntitiesPreview };
